import React, { useState, useEffect, Suspense, lazy } from "react";
import { ToastContainer, toast } from "react-toastify";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { PermissionsProvider, usePermissions } from "./contexts/PermissionsContext";
import { DialogProvider } from "./contexts/DialogContext";
import { API_BASE_URL } from "./config";
import { normalizeRole, roleMatchesAny, isGlobalPlatformRole } from "./utils/roles";
import { mustChangePasswordPending } from "./utils/postLoginRedirect";
import { initFCM, teardownFCM } from "./utils/fcmSetup";
import "react-toastify/dist/ReactToastify.css";

// Eagerly loaded — first-render auth surfaces. Everything else below is
// lazy()-split so the initial JS bundle stays small.
import Login from "./pages/auth/Login";
import ForgotPassword from "./pages/auth/ForgotPassword";
import SetPassword from "./pages/auth/SetPassword";
import DashboardRedirect from "./components/DashboardRedirect";

// Lazy-loaded routes — each becomes its own chunk fetched on first navigation
const Dashboard = lazy(() => import("./pages/dashboard/Dashboard"));
const Profile = lazy(() => import("./pages/profile/Profile"));
const Settings = lazy(() => import("./pages/profile/Settings"));
const ChangePassword = lazy(() => import("./pages/profile/ChangePassword"));
const Notifications = lazy(() => import("./pages/notifications/Notifications"));
const StartupInvitesList = lazy(() => import("./pages/startup/StartupInvitesList"));
const StartupInvitePublicForm = lazy(() => import("./pages/startup/StartupInvitePublicForm"));
// Users & Roles
const NewUser = lazy(() => import("./pages/user/AddUser"));
const EditUser = lazy(() => import("./pages/user/EditUser"));
const AddRole = lazy(() => import("./pages/roles/AddRole"));
const EditRole = lazy(() => import("./pages/roles/EditRole"));
const RolesAndUsers = lazy(() => import("./pages/access/RolesAndUsers"));

// My Team (Center Admin)
const TeamList = lazy(() => import("./pages/team/TeamList"));
const TeamMemberForm = lazy(() => import("./pages/team/TeamMemberForm"));
const TeamMemberDetails = lazy(() => import("./pages/team/TeamMemberDetails"));

// Events
const EventList = lazy(() => import("./pages/events/EventList"));
const CreateEvent = lazy(() => import("./pages/events/CreateEvent"));
const EditEvent = lazy(() => import("./pages/events/EditEvent"));
const EventDetails = lazy(() => import("./pages/events/EventDetails"));
const EventTemplateLibrary = lazy(() => import("./pages/events/EventTemplateLibrary"));
const EventTemplateDetails = lazy(() => import("./pages/events/EventTemplateDetails"));
const AddEventTemplate = lazy(() => import("./pages/events/AddEventTemplate"));
const EditEventTemplate = lazy(() => import("./pages/events/EditEventTemplate"));
const DocumentVerification = lazy(() => import("./pages/admin/DocumentVerification"));
const ProfileReview = lazy(() => import("./pages/admin/ProfileReview"));

// Speakers
const SpeakerList = lazy(() => import("./pages/speakers/SpeakerList"));
const CreateSpeaker = lazy(() => import("./pages/speakers/CreateSpeaker"));
const EditSpeaker = lazy(() => import("./pages/speakers/EditSpeaker"));
const StakeholdersList = lazy(() => import("./pages/stakeholders/StakeholdersList"));
const SpeakerDetails = lazy(() => import("./pages/speakers/SpeakerDetails"));

// Centers
const Centers = lazy(() => import("./pages/center/Center"));
const CenterRegister = lazy(() => import("./pages/center/CenterRegister"));
const PendingCenters = lazy(() => import("./pages/center/PendingCenters"));

// Master Admin (F1)
const MasterLogin = lazy(() => import("./pages/master/MasterLogin"));
const MasterDashboard = lazy(() => import("./pages/master/MasterDashboard"));
const CenterProfileWizard = lazy(() => import("./pages/center/CenterProfileWizard"));
const InitialSetupWizard = lazy(() => import("./pages/center/InitialSetupWizard"));
const CenterSettings = lazy(() => import("./pages/center/CenterSettings"));
const PlatformOverviewDashboard = lazy(() => import("./pages/center/PlatformOverviewDashboard"));
const CenterDetails = lazy(() => import("./pages/center/CenterDetails"));

// CFA (Call for Application)
const CFAList = lazy(() => import("./pages/cfa/CFAList"));
const CreateCFA = lazy(() => import("./pages/cfa/CreateCFA"));
const ViewCFA = lazy(() => import("./pages/cfa/ViewCFA"));
const EditCFA = lazy(() => import("./pages/cfa/EditCFA"));
const ApplyToCFA = lazy(() => import("./pages/cfa/ApplyToCFA"));
const PublicApplyForm = lazy(() => import("./pages/cfa/PublicApplyForm"));
const PublicEventView = lazy(() => import("./pages/events/PublicEventView"));
const ApplicationsList = lazy(() => import("./pages/cfa/ApplicationsList"));
const ApplicationsHome = lazy(() => import("./pages/cfa/ApplicationsHome"));
const JuryMembersList = lazy(() => import("./pages/jury/JuryMembersList"));
const JuryMemberDetail = lazy(() => import("./pages/jury/JuryMemberDetail"));
const ApplicationDetails = lazy(() => import("./pages/cfa/ApplicationDetails"));
const ApplicationReport = lazy(() => import("./pages/reports/ApplicationReport"));
const ApplicationDetailedReport = lazy(() => import("./pages/reports/ApplicationDetailedReport"));
const FundingReport = lazy(() => import("./pages/reports/FundingReport"));
const JuryReport = lazy(() => import("./pages/reports/JuryReport"));
const MentorReport = lazy(() => import("./pages/reports/MentorReport"));
const ApplicationReportForStartup = lazy(() => import("./pages/reports/ApplicationReportForStartup"));
const FundingReportForStartup = lazy(() => import("./pages/reports/FundingReportForStartup"));
const MentorReportForStartup = lazy(() => import("./pages/reports/MentorReportForStartup"));

// Registration
const EventRegistration = lazy(() => import("./pages/registration/EventRegistration"));
const ParticipantRegistrations = lazy(() => import("./pages/registration/ParticipantRegistrations"));
const MyRegistrations = lazy(() => import("./pages/registration/MyRegistrations"));
const ParticipantProfile = lazy(() => import("./pages/registration/ParticipantProfile"));
const AllRegistrations = lazy(() => import("./pages/admin/AllRegistrations"));

// Startup Management
const StartupList = lazy(() => import("./pages/startup/StartupList"));
const StartupProfileView = lazy(() => import("./pages/startup/StartupProfileView"));

// Funding
const FundingManagement = lazy(() => import("./pages/funding/FundingManagement"));
const FundCatalog = lazy(() => import("./pages/funding/FundCatalog"));
const FundOnboard = lazy(() => import("./pages/funding/FundOnboard"));

// Milestones
const MilestoneManagement = lazy(() => import("./pages/milestones/MilestoneManagement"));
const MilestonesOverview = lazy(() => import("./pages/milestones/MilestonesOverview"));

// Startup Registration
const StartupRegistration = lazy(() => import("./pages/startup/StartupRegistration"));
const StartupProfileForm = lazy(() => import("./pages/startup/StartupProfileForm"));
const DocumentManagement = lazy(() => import("./pages/startup/DocumentManagement"));
const ComplianceDocuments = lazy(() => import("./pages/startup/ComplianceDocuments"));
const ProfileReviewSubmission = lazy(() => import("./components/startups/ProfileReviewSubmission"));
const MyMentors = lazy(() => import("./pages/startup/MyMentors"));
const MySessions = lazy(() => import("./pages/startup/MySessions"));
const MyMentorDetails = lazy(() => import("./pages/startup/MyMentorDetails"));
const MySessionDetails = lazy(() => import("./pages/startup/MySessionDetails"));

// Admin
const AdminStartupProfileEditor = lazy(() => import("./pages/admin/AdminStartupProfileEditor"));

// Mentor
const MentorAssignments = lazy(() => import("./pages/mentor/MentorAssignments"));
const AssignmentDetails = lazy(() => import("./pages/mentor/AssignmentDetails"));
const WorkloadDashboardPage = lazy(() => import("./pages/mentor/WorkloadDashboard"));
const MentorPerformanceSummary = lazy(() => import("./pages/mentor/MentorPerformanceSummary"));
const SessionScheduling = lazy(() => import("./pages/mentor/SessionScheduling"));
const SessionPreparation = lazy(() => import("./pages/mentor/SessionPreparation"));
const MentorVerification = lazy(() => import("./pages/mentor/MentorVerification"));
const MentorVerificationDetails = lazy(() => import("./pages/mentor/MentorVerificationDetails"));
const MentorSessions = lazy(() => import("./pages/mentor/MentorSessions"));
const MentoringSessions = lazy(() => import("./pages/mentor/MentoringSessions"));
const AssignedStartups = lazy(() => import("./pages/mentor/AssignedStartups"));

// Create Material-UI theme matching the app's design.
// 13e: factory so we can rebuild the theme when the user toggles light/dark.
// `mode` flips MUI's own defaults (text, dividers, background). The
// `[data-theme="dark"]` compatibility layer in index.css handles the
// Tailwind/inline-white surfaces that the token system doesn't reach.
const buildTheme = (mode) =>
  createTheme({
    palette: {
      mode,
      primary: { main: "#3B82F6" },
      secondary: { main: "#64748B" },
    },
    typography: {
      fontFamily: "'Sora', sans-serif", // App-wide UI font; Fraunces is opt-in for display
    },
    shape: { borderRadius: 8 },
  });

const readThemeMode = () =>
  typeof document !== "undefined" &&
  document.documentElement.getAttribute("data-theme") === "dark"
    ? "dark"
    : "light";

// ProtectedRoute
// Global platform roles (superadmin, center_admin, master) may still use
// `allowedRoles` for platform-only surfaces. All other roles are gated solely
// by the RBAC matrix via `resource` + `action`.
function ProtectedRoute({ children, allowedRoles, strictAllowedRoles, resource, action = "read", skipPasswordGate = false }) {
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isRoleAllowed, setIsRoleAllowed] = useState(true);
  const token = localStorage.getItem("token");
  const location = useLocation();
  const { can, resourcePermissions, loading: permissionsLoading } = usePermissions();

  const enforceRoleGate = (roleName) => {
    // strictAllowedRoles locks a route to an exact role set for EVERY role,
    // including non-global ones — used where permission-driven roles (custom
    // center designations) must NOT reach a route even if their matrix grants
    // the underlying resource (e.g. program Applications view).
    if (Array.isArray(strictAllowedRoles) && strictAllowedRoles.length > 0) {
      return roleMatchesAny(roleName, strictAllowedRoles);
    }
    if (!Array.isArray(allowedRoles) || allowedRoles.length === 0) return true;
    // Non-global roles are permission-driven; skip the legacy role-name gate.
    if (!isGlobalPlatformRole(roleName)) return true;
    return roleMatchesAny(roleName, allowedRoles);
  };

  useEffect(() => {
    const checkUserStatus = async () => {
      if (!token) {
        setIsAuthorized(false);
        setIsChecking(false);
        return;
      }

      // Throttle the profile re-validation. Without this, every protected
      // route remount fires a /users/profile call — that's 3+ requests per
      // navigation. We still want near-real-time deactivation detection,
      // so the cache lives 60s.
      const PROFILE_CHECK_TTL_MS = 60_000;
      const lastCheck = Number(
        sessionStorage.getItem("auth_last_profile_check") || 0,
      );
      const cachedAuthorized =
        sessionStorage.getItem("auth_last_authorized") === "1";
      if (
        lastCheck &&
        Date.now() - lastCheck < PROFILE_CHECK_TTL_MS &&
        cachedAuthorized
      ) {
        setIsAuthorized(true);
        setIsChecking(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/users/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 401 || response.status === 403) {
          let logoutMessage = "Your session has expired. Please log in again.";
          try {
            const body = await response.json();
            const apiMessage = body?.message || "";
            if (/inactive|no longer active|suspended|deactivated/i.test(apiMessage)) {
              logoutMessage =
                "Your account has been inactive. Please contact the administrator.";
            } else if (apiMessage) {
              logoutMessage = apiMessage;
            }
          } catch {
            // Keep the default session-expired message.
          }
          localStorage.clear();
          window.dispatchEvent(new Event("logout"));
          toast.error(logoutMessage);
          setIsAuthorized(false);
          setIsChecking(false);
          return;
        }

        if (!response.ok) {
          // Transient profile-API error: stay logged in, but DO NOT skip the
          // role guard — enforce allowedRoles from the cached role instead of
          // failing open (previously any non-2xx bypassed the check).
          const cachedRole =
            localStorage.getItem("roleName") ||
            localStorage.getItem("role") ||
            "";
          if (!enforceRoleGate(cachedRole)) {
            setIsRoleAllowed(false);
          }
          setIsAuthorized(true);
          setIsChecking(false);
          return;
        }

        const data = await response.json();
        const accountStatus = data.accountStatus || "Active";

        // Role-based guard applies only to global platform roles.
        const currentRole =
          data.role_name ||
          data.role ||
          localStorage.getItem("roleName") ||
          localStorage.getItem("role") ||
          "";
        if (!enforceRoleGate(currentRole)) {
          setIsRoleAllowed(false);
          setIsAuthorized(true);
          setIsChecking(false);
          return;
        }

        // Check if account is deactivated or inactive
        if (
          accountStatus?.toLowerCase() === "deactive" ||
          accountStatus?.toLowerCase() === "inactive"
        ) {
          localStorage.clear();
          window.dispatchEvent(new Event("logout"));
          toast.error(
            "Your account has been inactive. Please contact the administrator.",
          );
          setIsAuthorized(false);
          setIsChecking(false);
          return;
        }

        // User is active
        if (data.mustChangePassword) {
          localStorage.setItem("mustChangePassword", "1");
        } else {
          localStorage.removeItem("mustChangePassword");
        }

        sessionStorage.setItem("auth_last_profile_check", String(Date.now()));
        sessionStorage.setItem("auth_last_authorized", "1");
        setIsAuthorized(true);
        setIsChecking(false);
      } catch (error) {
        // Network error: stay logged in, but still enforce allowedRoles from
        // the cached role rather than failing open.
        console.error("Error checking user status:", error);
        const cachedRole =
          localStorage.getItem("roleName") ||
          localStorage.getItem("role") ||
          "";
        if (!enforceRoleGate(cachedRole)) {
          setIsRoleAllowed(false);
        }
        setIsAuthorized(true);
        setIsChecking(false);
      }
    };

    checkUserStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (isChecking || (resource && permissionsLoading)) {
    // Show loading state while checking auth and/or permissions matrix
    return (
      <div className="flex items-center justify-center min-h-full">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!token || !isAuthorized) {
    return <Navigate to="/login" replace />;
  }

  if (
    !skipPasswordGate &&
    mustChangePasswordPending() &&
    location.pathname !== "/set-password"
  ) {
    return <Navigate to="/set-password" replace />;
  }

  if (!isRoleAllowed) {
    // Defer toast to next tick so React doesn't flag a side-effect during render
    setTimeout(() => {
      toast.error("You do not have permission to access this page.");
    }, 0);
    return <Navigate to={getRoleBasedDashboardPath()} replace />;
  }

  // RBAC gate: enforce once the permissions matrix has loaded.
  if (resource && !permissionsLoading) {
    if (!resourcePermissions || !can(resource, action)) {
      setTimeout(() => {
        toast.error(`You do not have ${action} access to ${resource}.`);
      }, 0);
      return <Navigate to={getRoleBasedDashboardPath()} replace />;
    }
  }

  return children;
}

// Helper function to get role-based dashboard path.
// Map keys are NORMALIZED (compact) forms — normalizeRole() strips spaces
// and underscores so 'super_admin', 'super admin', 'superadmin' all hit
// the same entry.
const getRoleBasedDashboardPath = () => {
  const roleName =
    localStorage.getItem("roleName") || localStorage.getItem("role") || "";
  const normalizedRole = normalizeRole(roleName);
  // Keys must match normalizeRole() output, which lowercases/trims but keeps
  // underscores (e.g. 'center_admin', 'startup_founder', 'jury_member'). Using
  // compact keys here previously sent these roles to the '/events' default.
  const roleMap = {
    mentor: "/dashboard/mentor",
    startup_founder: "/dashboard/startup_founder",
    superadmin: "/dashboard/superadmin",
    center_admin: "/dashboard/center_admin",
    jury_member: "/dashboard/jury_member",
  };
  // Custom center designations (not in roleMap) land in the center context.
  return roleMap[normalizedRole] || "/dashboard/center_admin";
};

// Component to redirect to role-based dashboard
function RoleBasedDashboardRedirect() {
  return <Navigate to={getRoleBasedDashboardPath()} replace />;
}

// Component to expose navigate globally as window.navigate and window.nevigate
function NavigationManager() {
  const navigate = useNavigate();
  useEffect(() => {
    window.navigate = (to) => {
      if (to === -1) {
        window.history.back();
      } else {
        navigate(to);
      }
    };
    // Support the specific typo 'nevigate' as requested
    window.nevigate = window.navigate;
  }, [navigate]);
  return null;
}

// Suspense fallback while a lazy route chunk is being fetched.
// Sits on the paper background so it reads as part of the app shell,
// not as a stark white flash.
function RouteLoading() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--an-paper)',
      }}
    >
      <div
        className="w-12 h-12 border-4 rounded-full animate-spin"
        style={{ borderColor: 'var(--an-hair)', borderTopColor: 'var(--an-coral)' }}
        aria-label="Loading"
      />
    </div>
  );
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem("token"));

  // 13e: MUI palette follows the `data-theme` attribute on <html>. The Header
  // toggle (and the pre-paint script in public/index.html) flip the attribute;
  // the `an-theme-change` custom event fires on toggle so we rebuild the theme.
  const [muiTheme, setMuiTheme] = useState(() => buildTheme(readThemeMode()));
  useEffect(() => {
    const sync = () => setMuiTheme(buildTheme(readThemeMode()));
    window.addEventListener("an-theme-change", sync);
    return () => window.removeEventListener("an-theme-change", sync);
  }, []);

  useEffect(() => {
    const handleLogout = () => {
      setIsLoggedIn(false);
      teardownFCM();
    };
    window.addEventListener("logout", handleLogout);
    return () => window.removeEventListener("logout", handleLogout);
  }, []);

  // Register for Firebase Web Push when the user is logged in.
  // onForegroundMessage fires when a push arrives while the tab is open;
  // we dispatch a custom event so NotificationBell refreshes its count.
  useEffect(() => {
    if (isLoggedIn) {
      initFCM((msg) => {
        window.dispatchEvent(new CustomEvent("fcm-foreground-message", { detail: msg }));
      });
    }
  }, [isLoggedIn]);

  return (
    <ThemeProvider theme={muiTheme}>
      <Router>
        <NavigationManager />
        <DialogProvider>
          <PermissionsProvider>
            <Suspense fallback={<RouteLoading />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route
                path="/set-password"
                element={
                  <ProtectedRoute skipPasswordGate>
                    <SetPassword />
                  </ProtectedRoute>
                }
              />
              <Route path="/startup-invite/:token" element={<StartupInvitePublicForm />} />
              <Route path="/apply/:token" element={<PublicApplyForm />} />
              <Route path="/events/public/:token" element={<PublicEventView />} />

              {/* Master Admin (F1) — public login + protected dashboard */}
              <Route path="/master/login" element={<MasterLogin />} />
              <Route
                path="/master"
                element={
                  <ProtectedRoute allowedRoles={["superadmin"]}>
                    <MasterDashboard />
                  </ProtectedRoute>
                }
              />

              {/* Dashboards */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <RoleBasedDashboardRedirect />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/superadmin"
                element={
                  <ProtectedRoute>
                    <Dashboard role="superadmin" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/admin"
                element={
                  <ProtectedRoute>
                    <Dashboard role="admin" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/center_admin"
                element={
                  <ProtectedRoute>
                    <Dashboard role="center_admin" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/mentor"
                element={
                  <ProtectedRoute>
                    <Dashboard role="mentor" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/startup_founder"
                element={
                  <ProtectedRoute>
                    <Dashboard role="startup_founder" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/jury_member"
                element={
                  <ProtectedRoute>
                    <Dashboard role="jury_member" />
                  </ProtectedRoute>
                }
              />

              {/* Startup Registration Route */}
              <Route
                path="/startup-registration"
                element={
                  <ProtectedRoute resource="startups" action="read">
                    <StartupRegistration />
                  </ProtectedRoute>
                }
              />

              {/* Startup Management Route */}
              <Route
                path="/startups"
                element={
                  <ProtectedRoute resource="startups" action="read">
                    <StartupList />
                  </ProtectedRoute>
                }
              />
              {/* Startup invites MUST be before /:startupId to avoid route conflict */}
              <Route
                path="/startups/invites"
                element={
                  <ProtectedRoute>
                    <StartupInvitesList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/startups/:startupId"
                element={
                  <ProtectedRoute>
                    <StartupProfileView />
                  </ProtectedRoute>
                }
              />

              {/* Legacy mentor wizard URL — profile completion lives under /profile */}
              <Route
                path="/mentor/profile-wizard"
                element={<Navigate to="/profile" replace />}
              />

              {/* Notifications */}
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <Notifications />
                  </ProtectedRoute>
                }
              />

              {/* Profile */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/change-password"
                element={
                  <ProtectedRoute>
                    <ChangePassword />
                  </ProtectedRoute>
                }
              />
              {/* Redirect unknown routes to login or role-based dashboard */}
              <Route
                path="*"
                element={
                  isLoggedIn ? (
                    <RoleBasedDashboardRedirect />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* Users — admin only. Lives inside the unified Roles & Users page. */}
              <Route
                path="/user-list"
                element={
                  <ProtectedRoute resource="users" action="read">
                    <RolesAndUsers defaultTab="users" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/add-user"
                element={
                  <ProtectedRoute resource="users" action="write">
                    <NewUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/edit-user/:userId"
                element={
                  <ProtectedRoute resource="users" action="write">
                    <EditUser />
                  </ProtectedRoute>
                }
              />

              {/* Roles & Users — unified page; tab persisted via URL hash.
                  Gated by role (like its /user-list siblings) rather than the
                  'roles' resource: center_admin's matrix grants 'users' but not
                  'roles', so a resource gate made this merged page unreachable
                  for them even though their menu links here. */}
              <Route
                path="/role-list"
                element={
                  <ProtectedRoute resource="users" action="read">
                    <RolesAndUsers defaultTab="roles" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/add-role"
                element={
                  <ProtectedRoute resource="roles" action="write">
                    <AddRole />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/edit-role/:roleId"
                element={
                  <ProtectedRoute resource="roles" action="write">
                    <EditRole />
                  </ProtectedRoute>
                }
              />

              {/* My Team — team management. Each page gates on the signed-in
                  user's RBAC matrix (the 'users' resource) from PermissionsContext,
                  the same source the rest of the app uses. The legacy
                  '/my-team/permissions' editor was retired; it now redirects to
                  the real role management at /role-list. Static segments ('/add')
                  are declared before the ':id' param so they never resolve as ids. */}
              <Route
                path="/my-team"
                element={
                  <ProtectedRoute resource="users" action="read">
                    <TeamList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-team/add"
                element={
                  <ProtectedRoute>
                    <TeamMemberForm />
                  </ProtectedRoute>
                }
              />
              {/* Retired: the localStorage-backed permission editor. Role
                  permissions are now managed in one place — /role-list. */}
              <Route
                path="/my-team/permissions"
                element={<Navigate to="/role-list" replace />}
              />
              <Route
                path="/my-team/:id/edit"
                element={
                  <ProtectedRoute>
                    <TeamMemberForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-team/:id"
                element={
                  <ProtectedRoute>
                    <TeamMemberDetails />
                  </ProtectedRoute>
                }
              />

              {/* Events — viewing is open to any authenticated user (same as the
                  event templates routes); creating/editing stays manager-only.
                  (Previously resource-gated, which redirected center admins whose
                  permission matrix didn't include the 'events' resource.) */}
              <Route
                path="/events"
                element={
                  <ProtectedRoute>
                    <EventList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/events/create"
                element={
                  <ProtectedRoute resource="events" action="write">
                    <CreateEvent />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/events/:id"
                element={
                  <ProtectedRoute>
                    <EventDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/events/:id/edit"
                element={
                  <ProtectedRoute resource="events" action="write">
                    <EditEvent />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/events/templates"
                element={
                  <ProtectedRoute>
                    <EventTemplateLibrary />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/events/templates/create"
                element={
                  <ProtectedRoute>
                    <AddEventTemplate />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/events/templates/:id"
                element={
                  <ProtectedRoute>
                    <EventTemplateDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/events/templates/:id/edit"
                element={
                  <ProtectedRoute>
                    <EditEventTemplate />
                  </ProtectedRoute>
                }
              />

              {/* Speakers */}
              <Route
                path="/stakeholders"
                element={
                  <ProtectedRoute resource="mentors" action="read">
                    <StakeholdersList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/speakers"
                element={
                  <ProtectedRoute>
                    <SpeakerList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/speakers/create"
                element={
                  <ProtectedRoute>
                    <CreateSpeaker />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/speakers/:id"
                element={
                  <ProtectedRoute>
                    <SpeakerDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/speakers/:id/edit"
                element={
                  <ProtectedRoute>
                    <EditSpeaker />
                  </ProtectedRoute>
                }
              />

              {/* Funding Management */}
              <Route
                path="/funding"
                element={
                  <ProtectedRoute resource="funding" action="read">
                    <FundingManagement />
                  </ProtectedRoute>
                }
              />

              {/* Fund / Grant Catalog (fund listing module) */}
              <Route
                path="/funds"
                element={
                  <ProtectedRoute resource="funds" action="read">
                    <FundCatalog />
                  </ProtectedRoute>
                }
              />
              {/* Add fund (onboard a scheme) — full-page wizard. Static
                  "/create" is declared before any "/:id" funds route so it
                  never resolves as an id. */}
              <Route
                path="/funds/create"
                element={
                  <ProtectedRoute resource="funds" action="write">
                    <FundOnboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/funds/:id/edit"
                element={
                  <ProtectedRoute resource="funds" action="write">
                    <FundOnboard />
                  </ProtectedRoute>
                }
              />

              {/* Centers */}
              <Route
                path="/centers"
                element={
                  <ProtectedRoute>
                    <Centers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/centers/register"
                element={
                  <ProtectedRoute allowedRoles={['superadmin']}>
                    <CenterRegister />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/centers/pending"
                element={
                  <ProtectedRoute allowedRoles={['superadmin']}>
                    <PendingCenters />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/centers/:centerId/details"
                element={
                  <ProtectedRoute>
                    <CenterDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/centers/:centerId/profile"
                element={
                  <ProtectedRoute resource="centers" action="write">
                    <CenterProfileWizard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/centers/:centerId/setup-wizard"
                element={
                  <ProtectedRoute resource="centers" action="write">
                    <InitialSetupWizard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/centers/:centerId/settings"
                element={
                  <ProtectedRoute resource="centers" action="write">
                    <CenterSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/documents/verification"
                element={
                  <ProtectedRoute resource="documents" action="read">
                    <DocumentVerification />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/profiles/review"
                element={
                  <ProtectedRoute resource="applications" action="read">
                    <ProfileReview />
                  </ProtectedRoute>
                }
              />
              {/* CFA (Call for Application) */}
              <Route
                path="/cfa"
                element={
                  <ProtectedRoute resource="applications" action="read">
                    <CFAList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cfa/create"
                element={
                  <ProtectedRoute resource="applications" action="write">
                    <CreateCFA />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cfa/:id"
                element={
                  <ProtectedRoute>
                    <ViewCFA />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cfa/:id/edit"
                element={
                  <ProtectedRoute resource="applications" action="write">
                    <EditCFA />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cfa/:programId/apply"
                element={
                  <ProtectedRoute resource="applications" action="write">
                    <ApplyToCFA />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cfa/:programId/applications"
                element={
                  <ProtectedRoute strictAllowedRoles={['superadmin', 'center_admin', 'jury_member', 'startup_founder']}>
                    <ApplicationsList />
                  </ProtectedRoute>
                }
              />
              {/* Program-scoped application detail — keeps the program in the
                  URL when drilling in from /cfa/:programId/applications. */}
              <Route
                path="/cfa/:programId/applications/:id"
                element={
                  <ProtectedRoute strictAllowedRoles={['superadmin', 'center_admin', 'jury_member', 'startup_founder']}>
                    <ApplicationDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/applications"
                element={
                  <ProtectedRoute resource="applications" action="read">
                    <ApplicationsHome />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jury/members"
                element={
                  <ProtectedRoute resource="jury" action="read">
                    <JuryMembersList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/jury/members/:userId"
                element={
                  <ProtectedRoute resource="jury" action="read">
                    <JuryMemberDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/applications/:id"
                element={
                  <ProtectedRoute resource="applications" action="read">
                    <ApplicationDetails />
                  </ProtectedRoute>
                }
              />

              {/* Reports */}
              <Route
                path="/reports/application-report"
                element={
                  <ProtectedRoute resource="reports" action="read">
                    <ApplicationReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports/center/application-funnel-internal-detailed-report"
                element={
                  <ProtectedRoute resource="reports" action="read">
                    <ApplicationDetailedReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports/funding-report"
                element={
                  <ProtectedRoute resource="reports" action="read">
                    <FundingReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports/jury-report"
                element={
                  <ProtectedRoute resource="reports" action="read">
                    <JuryReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports/mentor-report"
                element={
                  <ProtectedRoute resource="reports" action="read">
                    <MentorReport />
                  </ProtectedRoute>
                }
              />

              {/* Startup Founder Reports */}
              <Route
                path="/reports/startup/application-report"
                element={
                  <ProtectedRoute resource="reports" action="read">
                    <ApplicationReportForStartup />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports/startup/funding-report"
                element={
                  <ProtectedRoute resource="reports" action="read">
                    <FundingReportForStartup />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports/startup/mentor-report"
                element={
                  <ProtectedRoute resource="reports" action="read">
                    <MentorReportForStartup />
                  </ProtectedRoute>
                }
              />

              {/* Registration */}
              <Route
                path="/events/:eventId/register"
                element={
                  <ProtectedRoute>
                    <EventRegistration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/participants/registrations"
                element={
                  <ProtectedRoute>
                    <MyRegistrations />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/participants/registrations"
                element={
                  <ProtectedRoute resource="events" action="read">
                    <ParticipantRegistrations />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/participants/profile"
                element={
                  <ProtectedRoute>
                    <ParticipantProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/registrations"
                element={
                  <ProtectedRoute resource="events" action="read">
                    <AllRegistrations />
                  </ProtectedRoute>
                }
              />

              {/* Protected Startup Routes */}
              <Route
                path="/startups/:startupId/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardRedirect />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/startups/:startupId/profile"
                element={
                  <ProtectedRoute>
                    <StartupProfileForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/startups/:startupId/profile/:section"
                element={
                  <ProtectedRoute>
                    <StartupProfileForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/startups/:startupId/documents"
                element={
                  <ProtectedRoute>
                    <DocumentManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/startups/:startupId/compliance"
                element={
                  <ProtectedRoute>
                    <ComplianceDocuments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/startups/:startupId/milestones"
                element={
                  <ProtectedRoute>
                    <MilestoneManagement />
                  </ProtectedRoute>
                }
              />

              {/* Legacy Route Redirects - point to main profile form */}
              <Route
                path="/startups/:startupId/profile/management"
                element={
                  <ProtectedRoute>
                    <StartupProfileForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/startups/:startupId/profile/submit"
                element={
                  <ProtectedRoute>
                    <ProfileReviewSubmission />
                  </ProtectedRoute>
                }
              />

              {/* Startup Founder Routes*/}
              <Route
                path="/my-mentors"
                element={
                  <ProtectedRoute>
                    <MyMentors />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-mentors/:assignmentId"
                element={
                  <ProtectedRoute>
                    <MyMentorDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-sessions"
                element={
                  <ProtectedRoute>
                    <MySessions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-sessions/:sessionId"
                element={
                  <ProtectedRoute>
                    <MySessionDetails />
                  </ProtectedRoute>
                }
              />

              {/* Milestone Routes */}
              <Route
                path="/milestones"
                element={
                  <ProtectedRoute resource="milestones" action="read">
                    <MilestonesOverview />
                  </ProtectedRoute>
                }
              />

              {/* Mentor Routes */}
              <Route
                path="/mentors/assignments"
                element={
                  <ProtectedRoute>
                    <MentorAssignments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mentors/assignments-list"
                element={
                  <ProtectedRoute>
                    <MentorAssignments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mentors/assignments/:id"
                element={
                  <ProtectedRoute>
                    <AssignmentDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mentors/assigned-startups"
                element={
                  <ProtectedRoute>
                    <AssignedStartups />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mentors/workload"
                element={
                  <ProtectedRoute>
                    <WorkloadDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mentors/performance-summary"
                element={
                  <ProtectedRoute>
                    <MentorPerformanceSummary />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mentors/verification"
                element={
                  <ProtectedRoute>
                    <MentorVerification />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mentors/verification/:mentorId"
                element={
                  <ProtectedRoute>
                    <MentorVerificationDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sessions/schedule"
                element={
                  <ProtectedRoute>
                    <SessionScheduling />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mentors/sessions"
                element={
                  <ProtectedRoute>
                    <MentorSessions />
                  </ProtectedRoute>
                }
              />
              {/* Mentoring Sessions — flat list of all centre sessions, each
                  card showing the mentor (name + email) and session detail.
                  Center-admin / superadmin scoped; gated on startups read to
                  mirror the sidebar item mapping. */}
              <Route
                path="/mentoring-sessions"
                element={
                  <ProtectedRoute resource="startups" action="read">
                    <MentoringSessions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mentors/sessions/:sessionId/preparation"
                element={
                  <ProtectedRoute>
                    <SessionPreparation />
                  </ProtectedRoute>
                }
              />

              {/* Admin Routes */}
              <Route
                path="/admin/platform-overview"
                element={
                  <ProtectedRoute>
                    <PlatformOverviewDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/startups/:startupId/edit"
                element={
                  <ProtectedRoute>
                    <AdminStartupProfileEditor />
                  </ProtectedRoute>
                }
              />

              {/* Catch-all */}
              <Route
                path="*"
                element={
                  isLoggedIn ? (
                    <RoleBasedDashboardRedirect />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
            </Routes>
            </Suspense>
          </PermissionsProvider>
        </DialogProvider>
      </Router>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ zIndex: 99999 }}
      />
    </ThemeProvider>
  );
}

export default App;
